import { cookieManager } from "@utils/cookie.manager";
import appConfigs from "@configs/app";
import store from "@plugins/store";
import i18n from "@plugins/i18n";
import { loadLocaleMessages } from "@plugins/i18n";
import { redirect } from "@plugins/router";
import { reflectKeys } from "@utils/key.reflactor.ts";
import { isEmpty } from "@/utils/var.check";
import Modal from "@plugins/modal";
import AuthApi from "@apis/auth.api";

const SESSION_STATUS = reflectKeys(["WARN", "EXPIRED", "INVALID", "ACTIVE"]);
const SESSION_PING_INTERVAL = 1200000; // 20 min

class SessionManager {
  namespace = "";
  lastActivityCookieName = "";
  disableWarnCookieName = "";
  sessionExpiredInMilliSeconds = appConfigs.sessionExpiredDuration;
  sessionWarningInMilliSeconds = appConfigs.sessionWarningDuration;
  expiryTimer;
  disableWarnResumeTimer;
  disableWarnResetDuration;
  promptActivated = false;
  detectionStarted = false;
  detectionPaused = false;
  showExpiredPrompt = true;
  messages = {};
  onExtend = function () {
    /* intentionally leave blank */
  };
  onExpire = function () {
    /* intentionally leave blank */
  };

  constructor(params) {
    this.namespace = params.namespace || "";
    this.lastActivityCookieName = `_clat_${this.namespace}`;
    this.disableWarnCookieName = `_clat_warn_${this.namespace}_disabled`;
    this.sessionExpiredInMilliSeconds = params.sessionExpiredInMilliSeconds;
    this.sessionWarningInMilliSeconds = params.sessionWarningInMilliSeconds;
    this.disableWarnResetDuration =
      this.sessionExpiredInMilliSeconds - this.sessionWarningInMilliSeconds;
    this.showExpiredPrompt = params.showExpiredPrompt;
    this.onExtend =
      params.onExtend ||
      function () {
        /* intentionally leave blank */
      };
    this.onExpire =
      params.onExpire ||
      function () {
        /* intentionally leave blank */
      };
  }

  setLastActivity(cookieValue) {
    cookieManager.createCookie(this.lastActivityCookieName, cookieValue, null);
  }

  disableWarn(cookieValue) {
    cookieManager.createCookie(this.disableWarnCookieName, cookieValue, null);
  }

  pauseSessionDetect() {
    this.detectionPaused = true;
  }

  resumeSessionDetect() {
    this.detectionPaused = false;
  }

  startSessionDetect() {
    // auto extend when user interact
    document.addEventListener("click", this.doExtend.bind(this));
    document.addEventListener("touchstart", this.doExtend.bind(this));
    document.addEventListener("keydown", this.doExtend.bind(this));

    // check session status every 5 seconds
    if (this.expiryTimer) clearInterval(this.expiryTimer);
    this.expiryTimer = setInterval(() => {
      if (this.detectionPaused) return;
      const sessionState = this.getSessionState();
      if (sessionState === SESSION_STATUS.WARN) {
        this.notifyExpiring();
      } else if (sessionState === SESSION_STATUS.EXPIRED) {
        this.expireSession();
      } else if (sessionState === SESSION_STATUS.INVALID) {
        this.expireSession();
      }
    }, 5000);
  }

  getSessionState() {
    const sessionCookie = cookieManager.getCookie(this.lastActivityCookieName);

    // edge cases, if user play with the cookie
    if (!sessionCookie || isNaN(sessionCookie)) {
      return SESSION_STATUS.INVALID;
    }

    const idleTimeInMilliseconds = Date.now() - parseInt(sessionCookie);
    if (idleTimeInMilliseconds > this.sessionExpiredInMilliSeconds) {
      return SESSION_STATUS.EXPIRED;
    } else if (idleTimeInMilliseconds > this.sessionWarningInMilliSeconds) {
      return SESSION_STATUS.WARN;
    } else {
      return SESSION_STATUS.ACTIVE;
    }
  }

  expireSession() {
    if (this.promptActivated) {
      this.expireSession = this.notifyExpired;
    } else {
      this.expireSession = () => {
        /* intentionally leave blank */
      };
      this.notifyExpired();
    }
    this.setLastActivity(Date.now() - this.sessionExpiredInMilliSeconds);
    if (this.onExpire) this.onExpire();
  }

  notifyExpiring() {
    const dontWarn = cookieManager.getCookie(this.disableWarnCookieName) === "true";
    if (dontWarn) {
      return;
    }

    if (this.promptActivated) {
      return;
    }
    this.promptActivated = true;
    this.showWarnModal().then((confirm) => {
      this.promptActivated = false;
      if (confirm) {
        this.doExtend();
      } else {
        this.dontExtend();
      }
    });
  }

  async notifyExpired() {
    if (this.promptActivated) {
      return;
    }

    if (this.showExpiredPrompt) {
      this.promptActivated = true;
      await loadLocaleMessages("common");
      await loadLocaleMessages("common-aria-label");
      this.showExpiredModal().then(() => {
        this.promptActivated = false;
        store.dispatch("app/clearPageDirty");
        redirect(window.location.href);
      });
    } else {
      store.dispatch("app/clearPageDirty");
      redirect(window.location.href);
    }
  }

  showExpiredModal() {
    if (isEmpty(this.messages)) {
      this.initMessages();
    }
    return new Promise((resolve) => {
      Modal.$modal.showMessage("warning", {
        title: this.messages.SESSION_EXPIRED_TITLE,
        content: this.messages.SESSION_EXPIRED_CONTENT,
        primaryBtnLabel: this.messages.BUTTON_OK,
        use_secondaryButton: false,
        actionPrimary: resolve,
        actionHidden: resolve,
        modelClosed: resolve,
      });
    });
  }

  showWarnModal() {
    let confirm = false;
    if (isEmpty(this.messages)) {
      this.initMessages();
    }
    return new Promise((resolve) => {
      Modal.$modal.showMessage("warning", {
        title: this.messages.SESSION_EXPIRING_TITLE,
        content: this.messages.SESSION_EXPIRING_CONTENT,
        primaryBtnLabel: this.messages.BUTTON_OK,
        secondaryBtnLabel: this.messages.BUTTON_CANCEL,
        actionPrimary: () => {
          confirm = true;
        },
        modelClosed: () => {
          resolve(confirm);
        },
      });
    });
  }

  doExtend() {
    if (this.detectionPaused) {
      return;
    }

    // skip extend when expiring / expired prompt is activated
    if (this.promptActivated) {
      return;
    }

    const sessionState = this.getSessionState();
    if (sessionState === SESSION_STATUS.EXPIRED) {
      this.expireSession();
      return;
    }

    this.setLastActivity(Date.now());
    if (this.onExtend) this.onExtend();
  }

  dontExtend() {
    const sessionState = this.getSessionState();
    if (sessionState === SESSION_STATUS.EXPIRED) {
      this.expireSession();
      return;
    }

    // stop disturbing user with session expiry warning for a moment
    this.disableWarn(true);
    if (this.disableWarnResumeTimer) clearTimeout(this.disableWarnResumeTimer);
    this.disableWarnResumeTimer = setTimeout(() => {
      this.disableWarn(false);
    }, this.disableWarnResetDuration);
  }

  initMessages() {
    this.messages = {
      SESSION_EXPIRING_TITLE: i18n.t("session_expiring_title"),
      SESSION_EXPIRING_CONTENT: i18n.t("session_expiring_content"),
      SESSION_EXPIRED_TITLE: i18n.t("session_expired_title"),
      SESSION_EXPIRED_CONTENT: i18n.t("session_expired_content"),
      BUTTON_OK: i18n.t("button_ok"),
      BUTTON_CANCEL: i18n.t("button_cancel"),
    };
  }

  init() {
    this.disableWarn(false);
    this.setLastActivity(Date.now());
    if (!this.detectionStarted) {
      this.detectionStarted = true;
      this.startSessionDetect();
    } else {
      this.resumeSessionDetect();
    }

    if (this.namespace === "session") {
      setInterval(() => {
        AuthApi.pingSession();
      }, SESSION_PING_INTERVAL);
    }
  }
}

export { SessionManager as default };
